<template>
  <div class="menu-layout" ref="menuLayoutRef">
    <!-- ═══════════════════════════════════════════════════════ -->
    <!-- DESKTOP SIDEBAR NAVIGATION                              -->
    <!-- ═══════════════════════════════════════════════════════ -->
    <nav
      class="sidebar-nav"
      :class="{ 'sidebar-nav--hidden': !isNavVisible }"
    >
      <ul class="sidebar-nav__list">
        <li
          v-for="cat in rootCategories"
          :key="cat.id"
          class="sidebar-nav__item"
        >
          <button
            class="sidebar-nav__btn"
            :class="{ 'sidebar-nav__btn--active': activeCategoryId === cat.id }"
            @click="scrollToCategory(cat.id)"
          >
            <span class="sidebar-nav__label">{{ cat.name }}</span>
          </button>

          <!-- 2nd-level: subcategories (accordion — only active category) -->
          <Transition name="sidebar-sub">
            <ul
              v-if="getSubcategories(cat.id).length && activeCategoryId === cat.id"
              class="sidebar-nav__sublist"
            >
              <li
                v-for="sub in getSubcategories(cat.id)"
                :key="sub.id"
                class="sidebar-nav__subitem"
              >
                <button
                  class="sidebar-nav__subbtn"
                  :class="{
                    'sidebar-nav__subbtn--active':
                      activeCategoryId === cat.id && activeSubcategoryId === sub.id,
                  }"
                  @click="scrollToCategory(sub.id)"
                >
                  <span class="sidebar-nav__label">{{ sub.name }}</span>
                </button>
              </li>
            </ul>
          </Transition>
        </li>
      </ul>
    </nav>

    <!-- ═══════════════════════════════════════════════════════ -->
    <!-- MOBILE HORIZONTAL NAVIGATION                            -->
    <!-- ═══════════════════════════════════════════════════════ -->
    <div
      class="mobile-nav"
      :class="{ 'mobile-nav--hidden': !isNavVisible }"
    >
      <!-- Root categories bar -->
      <div class="mobile-nav__bar" ref="mobilePrimaryBarRef">
        <button
          v-for="cat in rootCategories"
          :key="cat.id"
          :ref="(el) => setMobileCatRef(cat.id, el)"
          class="mobile-nav__btn"
          :class="{ 'mobile-nav__btn--active': activeCategoryId === cat.id }"
          @click="scrollToCategory(cat.id)"
        >
          {{ cat.name }}
        </button>
      </div>

      <!-- Subcategories bar (shown only when active root has children) -->
      <Transition name="sub-bar">
        <div
          v-if="activeSubcategories.length"
          class="mobile-nav__bar mobile-nav__bar--sub"
          ref="mobileSubBarRef"
        >
          <button
            v-for="sub in activeSubcategories"
            :key="sub.id"
            :ref="(el) => setMobileSubRef(sub.id, el)"
            class="mobile-nav__subbtn"
            :class="{
              'mobile-nav__subbtn--active': activeSubcategoryId === sub.id,
            }"
            @click="scrollToCategory(sub.id)"
          >
            {{ sub.name }}
          </button>
        </div>
      </Transition>
    </div>

    <!-- ═══════════════════════════════════════════════════════ -->
    <!-- CONTENT: CATEGORY SECTIONS                              -->
    <!-- ═══════════════════════════════════════════════════════ -->
    <main class="menu-content" ref="menuContentRef">
      <section
        v-for="cat in rootCategories"
        :key="cat.id"
        :ref="(el) => setSectionRef(cat.id, el)"
        class="category-section"
      >
        <h2 class="category-section__heading">{{ cat.name }}</h2>

        <!-- If root category has its own items (no subcategories) -->
        <div
          v-if="!getSubcategories(cat.id).length && cat.items?.length"
          class="items-grid"
        >

          <!-- <div v-for="(item, idx) in cat.items":key="idx" class="item-card">
            <div class="item-card__inner">{{ item.name || `Item ${idx + 1}` }}</div>
          </div> -->

          <template  v-for="(itemId, idx) in cat.items">
            <MenuItem v-if="allItems[itemId]" :item="allItems[itemId]" />
          <div v-else class="item-card">
            <div class="item-card__inner">{{ `Stub Item ${idx + 1}` }}</div>
          </div>
          </template>

        </div>

        <!-- Subcategory blocks -->
        <div
          v-for="sub in getSubcategories(cat.id)"
          :key="sub.id"
          :ref="(el) => setSectionRef(sub.id, el)"
          class="subcategory-block"
        >
          <h3 class="subcategory-block__heading">{{ sub.name }}</h3>

          <div v-if="sub.items?.length" class="items-grid">

            <!-- <div v-for="(item, idx) in sub.items" :key="idx" class="item-card">
              <div class="item-card__inner">{{ item.name || `Item ${idx + 1}` }}</div>
            </div> -->

          <template  v-for="(itemId, idx) in sub.items">
            <MenuItem v-if="allItems[itemId]" :item="allItems[itemId]" />
            <div v-else class="item-card">
              <div class="item-card__inner">{{ `Stub Item ${idx + 1}` }}</div>
            </div>
          </template>
          </div>

          <div v-else class="subcategory-block__empty">{{ t('menu.noItemsYet') }}</div>
        </div>

        <!-- Fallback when root has neither items nor subcategories -->
        <div
          v-if="!getSubcategories(cat.id).length && !cat.items?.length"
          class="category-section__empty"
        >
          {{ t('menu.noItemsCategory') }}
        </div>
      </section>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useI18n } from 'vue-i18n';
import MenuItem from '@client/js/components/MenuItem.vue';
import { useProductStore } from '@client/js/store/products';

const store = useProductStore();
const { t } = useI18n();

// ── If you don't use Pinia, accept categories as a prop: ──
const props = defineProps({
  categories: {
    type: Array,
    default: () => [],
  },
})

const allCategories = computed(() => props.categories);
const allItems = computed(() => store.items);

/* ───────────────────────────────────────────────────────── */
/*  DERIVED DATA                                             */
/* ───────────────────────────────────────────────────────── */
const rootCategories = computed(() =>
  allCategories.value
    .filter((c) => c.parent === 0)
    .sort((a, b) => a.sortOrder - b.sortOrder),
)

function getSubcategories(parentId) {
  return allCategories.value
    .filter((c) => c.parent === parentId)
    .sort((a, b) => a.sortOrder - b.sortOrder)
}

/* ───────────────────────────────────────────────────────── */
/*  ACTIVE STATE                                             */
/* ───────────────────────────────────────────────────────── */
const activeCategoryId = ref(null)
const activeSubcategoryId = ref(null)
const isNavVisible = ref(true)

const activeSubcategories = computed(() => {
  if (activeCategoryId.value == null) return []
  return getSubcategories(activeCategoryId.value)
})

/* ───────────────────────────────────────────────────────── */
/*  TEMPLATE REFS                                            */
/* ───────────────────────────────────────────────────────── */
const menuLayoutRef = ref(null)
const menuContentRef = ref(null)
const mobilePrimaryBarRef = ref(null)
const mobileSubBarRef = ref(null)

// Map of categoryId → DOM element (sections & subcategory blocks)
const sectionRefs = new Map()
function setSectionRef(id, el) {
  if (el) sectionRefs.set(id, el)
  else sectionRefs.delete(id)
}

// Mobile button refs for auto-scroll-into-view
const mobileCatRefs = new Map()
function setMobileCatRef(id, el) {
  if (el) mobileCatRefs.set(id, el)
  else mobileCatRefs.delete(id)
}
const mobileSubRefs = new Map()
function setMobileSubRef(id, el) {
  if (el) mobileSubRefs.set(id, el)
  else mobileSubRefs.delete(id)
}

/* ───────────────────────────────────────────────────────── */
/*  SCROLL-TO on click                                       */
/* ───────────────────────────────────────────────────────── */
let isUserScrolling = false
let scrollTimeout = null

function scrollToCategory(id) {
  const el = sectionRefs.get(id)
  if (!el) return

  isUserScrolling = true
  clearTimeout(scrollTimeout)

  // ── Immediately update active state so buttons highlight on click ──
  const cat = allCategories.value.find((c) => c.id === id)
  if (cat) {
    if (cat.parent === 0) {
      activeCategoryId.value = cat.id
      const subs = getSubcategories(cat.id)
      activeSubcategoryId.value = subs.length ? subs[0].id : null
    } else {
      activeCategoryId.value = cat.parent
      activeSubcategoryId.value = cat.id
    }
  }

  // Offset for sticky mobile nav (approx)
  const offset = window.innerWidth < 768 ? 110 : 20
  const top = el.getBoundingClientRect().top + window.scrollY - offset

  window.scrollTo({ top, behavior: 'smooth' })

  // Unlock observer-based updates after scroll settles
  scrollTimeout = setTimeout(() => {
    isUserScrolling = false
  }, 800)
}

/* ───────────────────────────────────────────────────────── */
/*  INTERSECTION OBSERVER  – active tracking                 */
/* ───────────────────────────────────────────────────────── */
let observer = null
let navObserver = null

// Stores current intersection ratios for priority picking
const visibleEntries = new Map()

/**
 * Direct DOM position scan — works regardless of observer state.
 * Finds the section/subcategory whose top is closest to (but not far above)
 * the viewport top. This is the reliable fallback for short content.
 */
function syncActiveFromDOM() {
  if (isUserScrolling) return
  if (!sectionRefs.size) return

  const offset = window.innerWidth < 768 ? 120 : 30
  let bestId = null
  let bestDist = Infinity

  sectionRefs.forEach((el, id) => {
    const rect = el.getBoundingClientRect()
    // Distance from section top to the "anchor line" near viewport top
    const dist = Math.abs(rect.top - offset)
    // Also consider sections whose top is above viewport but bottom is still visible
    const isVisible = rect.bottom > 0 && rect.top < window.innerHeight
    if (isVisible && dist < bestDist) {
      bestDist = dist
      bestId = id
    }
  })

  if (bestId == null) return

  applyActiveId(bestId)
}

/**
 * Given a winning section/subcategory id, resolve and set
 * activeCategoryId + activeSubcategoryId.
 */
function applyActiveId(bestId) {
  const cat = allCategories.value.find((c) => c.id === bestId)
  if (!cat) return

  if (cat.parent === 0) {
    activeCategoryId.value = cat.id
    const subs = getSubcategories(cat.id)
    if (subs.length) {
      // Among this root's subcategories, find the one closest to top
      let bestSub = subs[0]
      let bestSubDist = Infinity
      for (const s of subs) {
        const el = sectionRefs.get(s.id)
        if (!el) continue
        const dist = Math.abs(el.getBoundingClientRect().top)
        if (dist < bestSubDist) {
          bestSubDist = dist
          bestSub = s
        }
      }
      activeSubcategoryId.value = bestSub.id
    } else {
      activeSubcategoryId.value = null
    }
  } else {
    activeCategoryId.value = cat.parent
    activeSubcategoryId.value = cat.id
  }
}

function setupObserver() {
  // ── Primary observer: narrow band for scroll-tracking on long content ──
  observer = new IntersectionObserver(
    (entries) => {
      if (isUserScrolling) return

      entries.forEach((entry) => {
        const id = Number(entry.target.dataset.observeId)
        if (entry.isIntersecting) {
          visibleEntries.set(id, entry.intersectionRatio)
        } else {
          visibleEntries.delete(id)
        }
      })

      // If the narrow band caught something, use it (precise scroll tracking)
      if (visibleEntries.size) {
        let bestId = null
        let bestTop = Infinity
        visibleEntries.forEach((_, id) => {
          const el = sectionRefs.get(id)
          if (!el) return
          const dist = Math.abs(el.getBoundingClientRect().top)
          if (dist < bestTop) {
            bestTop = dist
            bestId = id
          }
        })
        if (bestId != null) applyActiveId(bestId)
      } else {
        // Narrow band is empty → content is short or positioned outside band
        syncActiveFromDOM()
      }
    },
    {
      rootMargin: '-10% 0px -60% 0px',
      threshold: [0, 0.1, 0.25, 0.5],
    },
  )

  // Tag each element and observe
  sectionRefs.forEach((el, id) => {
    el.dataset.observeId = id
    observer.observe(el)
  })

  // ── Visibility observer for "hide nav when no content visible" ──
  navObserver = new IntersectionObserver(
    (entries) => {
      const entry = entries[0]
      if (entry) {
        isNavVisible.value = entry.isIntersecting
      }
    },
    { threshold: 0 },
  )

  if (menuContentRef.value) {
    navObserver.observe(menuContentRef.value)
  }

  // ── Scroll listener as a safety net ──
  // Handles edge cases where observer callbacks don't fire
  // (e.g. content fully visible, no intersection changes on scroll)
  window.addEventListener('scroll', onScroll, { passive: true })
}

let scrollRafId = null
function onScroll() {
  if (isUserScrolling) return
  if (scrollRafId) return // throttle to 1 per frame
  scrollRafId = requestAnimationFrame(() => {
    scrollRafId = null
    // Only use DOM fallback when observer has nothing
    if (!visibleEntries.size) {
      syncActiveFromDOM()
    }
  })
}

/* ───────────────────────────────────────────────────────── */
/*  AUTO-SCROLL NAV BUTTONS INTO VIEW (horizontal only)      */
/* ───────────────────────────────────────────────────────── */

/**
 * Scrolls a button horizontally into center of its scrollable parent
 * WITHOUT triggering any vertical page scroll (which causes the stutter).
 */
function scrollButtonIntoView(btn, container) {
  if (!btn || !container) return
  const containerRect = container.getBoundingClientRect()
  const btnRect = btn.getBoundingClientRect()
  // Target: center the button within the container
  const btnCenter = btnRect.left + btnRect.width / 2
  const containerCenter = containerRect.left + containerRect.width / 2
  const delta = btnCenter - containerCenter
  container.scrollBy({ left: delta, behavior: 'smooth' })
}

watch(activeCategoryId, (id) => {
  if (id == null) return
  nextTick(() => {
    const btn = mobileCatRefs.get(id)
    scrollButtonIntoView(btn, mobilePrimaryBarRef.value)
  })
})

watch(activeSubcategoryId, (id) => {
  if (id == null) return
  nextTick(() => {
    const btn = mobileSubRefs.get(id)
    // mobileSubBarRef may not be mounted yet if sub-bar is transitioning in
    const container = mobileSubBarRef.value
    if (container) {
      scrollButtonIntoView(btn, container)
    }
  })
})

/* ───────────────────────────────────────────────────────── */
/*  LIFECYCLE                                                */
/* ───────────────────────────────────────────────────────── */
onMounted(() => {
  nextTick(() => {
    setupObserver()
    // Synchronous initial check — guarantees active state even with few categories
    syncActiveFromDOM()
  })
})

onBeforeUnmount(() => {
  observer?.disconnect()
  navObserver?.disconnect()
  window.removeEventListener('scroll', onScroll)
  if (scrollRafId) cancelAnimationFrame(scrollRafId)
  clearTimeout(scrollTimeout)
})
</script>

<style scoped>
.menu-layout {
  --clr-text: #1c1917;
  --clr-text-muted: #524e56;
  --sidebar-w: 240px;
  --clr-bg: #fcfcfb;
  --clr-surface: #ffffff;
  --clr-border: #e7e5e4;
  --clr-text: #1c1917;
  --clr-accent: #0f766e;
  --clr-accent-bg: #f0fdfa;
  --radius: 8px;
  --shadow-sm: 0 1px 2px rgba(0, 0, 0, 0.05);
  --shadow-md: 0 4px 12px rgba(0, 0, 0, 0.08);

  display: flex;
  min-height: 100vh;
  color: var(--clr-text);
  background: var(--clr-bg);
  flex-direction: column;
  
  /* fix container */
  max-width: 1366px;
  max-width: min(100%, 1366px);
  margin: 0 auto;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
}

.sidebar-nav {
  position: sticky;
  top: 0;
  left: 0;
  width: var(--sidebar-w);
  min-width: var(--sidebar-w);
  height: 100vh;
  overflow-y: auto;
  padding: 24px 12px;
  background: var(--clr-surface);
  border-right: 1px solid var(--clr-border);
  box-shadow: var(--shadow-sm);
  transition: opacity 0.3s, transform 0.3s;
  z-index: 50;
  display: none;

  /* floating header */
  @media (min-width: 768px) {
    display: block;
  }

  @media (min-width: 1280px) {
    top: 72px;
    max-height: calc(100svh - 72px);
  }
}

/* #wpadminbar desktop */
.admin-bar .sidebar-nav {
  @media (min-width: 768px) and (max-width: 782px) { 
    top: 46px;
    max-height: calc(100svh - 46px);
  }
  @media (min-width: 783px) { 
    top: 32px;
    max-height: calc(100svh - 32px);
  }
  @media (min-width: 1280px) {
    top: 104px;
    max-height: calc(100svh - 104px);
  }
}

.sidebar-nav--hidden {
  opacity: 0;
  pointer-events: none;
  transform: translateX(-12px);
}

.sidebar-nav__list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.sidebar-nav__item {
  margin-bottom: 4px;
}

.sidebar-nav__label {
  display: block;
  overflow-wrap: anywhere;
}

.sidebar-nav__btn {
  display: block;
  width: 100%;
  padding: 10px 14px;
  border: none;
  /* border-radius: var(--radius); */
  border-radius: 0;
  background: transparent;
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--clr-text-muted);
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  line-height: 1.3;
  /* colors */
  position: relative;
  border-left: 3px solid transparent;
}
.sidebar-nav__btn::after {
  content: "";
  display: block;
  position: absolute;
  inset: 0;
  -o-object-fit: cover;
  object-fit: cover;
  width: 100%;
  height: 100%;
  z-index: -1;
  background-color: transparent;
  opacity: 0;
}
.sidebar-nav__btn:hover {
  color: var(--clr-text);
  border-left: 3px solid rgba(var(--c-primary-rgb), 0.5);

  &::after {
    background-color: var(--c-primary);
    opacity: .035;
  }
}

.sidebar-nav__btn--active {
  color: var(--clr-text);
  border-left: 3px solid var(--c-primary);
  padding-left: 11px;
}
.sidebar-nav__btn--active::after {
  background-color: var(--c-primary);
  opacity: .07;
}

/* Subcategory list */
.sidebar-nav__sublist {
  list-style: none;
  margin: 0;
  padding: 0 0 0 1.125rem;
}

.sidebar-nav__subitem {
  margin-bottom: 1px;
}

.sidebar-nav__subbtn {
  display: block;
  width: 100%;
  padding: 0.375rem 0.75rem;
  border: none;
  background: transparent;
  font-size: 13px;
  font-weight: 500;
  color: var(--clr-text-muted);
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;
  border-right: 2px solid transparent;
  letter-spacing: 0.125px;
  position: relative;

  &::after {
    content: "";
    display: block;
    position: absolute;
    z-index: -1;
    left: 0;
    top: 50%;
    right: initial;
    width: 0.375rem;
    height: 0.375rem;
    background-color: transparent;
    opacity: 0;
    transform: translate3d(-0.75rem, -50%, 0) rotate(45deg);
    border-right: 2px solid var(--clr-text-muted);
    border-top: 2px solid var(--clr-text-muted);
    border-radius: 2px;
  }

  &:hover {
    color: var(--clr-text);
  }

  &--active {
    color: var(--clr-text);
    font-weight: 600;
  }
}

@supports (-webkit-line-clamp: 2) {
  .sidebar-nav__btn .sidebar-nav__label {
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    line-height: 1.3125rem;
    max-height: 2.625rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .sidebar-nav__subbtn .sidebar-nav__label {
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    display: -webkit-box;
    line-height: 1.125rem;
    max-height: 2.25rem;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
.sidebar-nav__subbtn--active::after {
  transform: translate3d(-0.25rem, -50%, 0) rotate(45deg);
  transition: all 0.15s 0s linear;
  opacity: 1;
  border-color: var(--clr-text);
}

/* Sidebar subcategory accordion transition */
.sidebar-sub-enter-active {
  transition: max-height 0.3s ease, opacity 0.3s ease;
  overflow: hidden;
  
}
.sidebar-sub-leave-active {
  transition: max-height 0.15s ease, opacity 0.15s ease;
  overflow: hidden;
}
.sidebar-sub-enter-from,
.sidebar-sub-leave-to {
  max-height: 0;
  opacity: 0;
}
.sidebar-sub-enter-to,
.sidebar-sub-leave-from {
  max-height: 1000px; /* generous upper bound — CSS transitions need a concrete value */
  opacity: 1;
}

/* ═══════════════════════════════════════════════════════════ */
/*  MOBILE HORIZONTAL NAV                                     */
/* ═══════════════════════════════════════════════════════════ */
.mobile-nav {
  display: none;
  position: sticky;
  top: 0;
  z-index: 100;
  background: var(--clr-surface);
  border-bottom: 1px solid var(--clr-border);
  box-shadow: var(--shadow-sm);
  transition: opacity 0.3s, transform 0.3s;
}

/* #wpadminbar mobile */
.admin-bar .mobile-nav {
  top: 0;

  @media (min-width: 601px) {
    top: 46px;
  }

  @media (min-width: 601px) and (max-width: 782px) { 
    top: 46px;
  }
}

.mobile-nav--hidden {
  opacity: 0;
  pointer-events: none;
  transform: translateY(-100%);
}

.mobile-nav__bar {
  display: flex;
  gap: 6px;
  padding: 10px 12px;
  overflow-x: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.mobile-nav__bar::-webkit-scrollbar {
  display: none;
}

.mobile-nav__bar--sub {
  padding-top: 0;
  padding-bottom: 10px;
}

.mobile-nav__btn {
  flex-shrink: 0;
  padding: 8px 18px;
  border: 1px solid var(--clr-border);
  border-radius: 100px;
  background: var(--clr-surface);
  font-size: 14px;
  font-weight: 600;
  color: var(--clr-text-muted);
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
}

.mobile-nav__btn:hover {
  color: var(--clr-text);
}

.mobile-nav__btn--active {
  background: rgba(var(--c-primary-rgb), 0.9);
  border-color: var(--c-primary);
  color: var(--clr-surface);
}

.mobile-nav__subbtn {
  flex-shrink: 0;
  padding: 6px 14px;
  border: 1px solid var(--clr-border);
  border-radius: 100px;
  background: var(--clr-surface);
  font-size: 12px;
  font-weight: 500;
  color: var(--clr-text-muted);
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.2s;
   position: relative;
}

.mobile-nav__subbtn::after {
  content: "";
  display: block;
  position: absolute;
  inset: 0;
  -o-object-fit: cover;
  object-fit: cover;
  width: 100%;
  height: 100%;
  z-index: -1;
  background-color: transparent;
  opacity: 0;
}

.mobile-nav__subbtn:hover {
  border-color: var(--c-primary);;
  color: var(--clr-text);
}

.mobile-nav__subbtn--active {
  background: var(--clr-accent-bg);
  border-color: var(--c-primary);
  color: var(--clr-text);
  font-weight: 600;
}
.mobile-nav__subbtn--active::after {
  background-color: var(--c-primary);
  opacity: .07;
}

/* Sub-bar transition */
.sub-bar-enter-active,
.sub-bar-leave-active {
  transition: max-height 0.3s ease, opacity 0.25s ease;
  overflow: hidden;
}
.sub-bar-enter-from,
.sub-bar-leave-to {
  max-height: 0;
  opacity: 0;
}
.sub-bar-enter-to,
.sub-bar-leave-from {
  max-height: 60px;
  opacity: 1;
}

/* ═══════════════════════════════════════════════════════════ */
/*  CONTENT SECTIONS                                          */
/* ═══════════════════════════════════════════════════════════ */
.menu-content {
  flex: 1;
  min-width: 0;
  padding: 16px 0 100px;
  
  @media (min-width: 576px) {
    padding: 24px 28px 120px;
  }

}

.category-section {
  margin-bottom: 40px;
  
  @media (min-width: 576px) {
    padding: 24px;
    border: 1px solid var(--clr-border);
    border-radius: var(--radius);
    background: var(--clr-surface);
    box-shadow: var(--shadow-sm);
  }
}

.category-section__heading {
  margin: 0 0 20px;
  color: var(--clr-text);
  letter-spacing: -0.02em;
  border-bottom: 2px solid var(--clr-border);
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.375;
  margin-bottom: 20px;
  padding-bottom: 0.75rem;
  text-transform: uppercase;
}

.category-section__empty {
  padding: 32px;
  text-align: center;
  color: var(--clr-text-muted);
  font-size: 14px;
  font-style: italic;
}

/* Subcategory block */
.subcategory-block {
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid var(--clr-border);
}

.subcategory-block:first-of-type {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.subcategory-block__heading {
  margin: 0 0 14px;
  font-size: 1.125rem;
  font-weight: 600;
  color: #524e46;
  /* color: var(--clr-accent); */
  letter-spacing: -0.01em;
}

.subcategory-block__empty {
  padding: 20px;
  text-align: center;
  color: var(--clr-text-muted);
  font-size: 13px;
  font-style: italic;
  background: var(--clr-bg);
  border-radius: 6px;
}

/* ═══════════════════════════════════════════════════════════ */
/*  ITEMS GRID (stub cards)                                   */
/* ═══════════════════════════════════════════════════════════ */
.items-grid {
  display: grid;
  gap: 1rem;
  grid-template-columns: 1fr;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  /* @media (min-width: 1366px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  } */
}

.item-card {
  border: 1px solid var(--clr-border);
  border-radius: var(--radius);
  background: var(--clr-bg);
  transition: box-shadow 0.2s, border-color 0.2s;
}

.item-card:hover {
  box-shadow: var(--shadow-md);
}

.item-card__inner {
  padding: 16px;
  font-size: 14px;
  font-weight: 500;
  color: var(--clr-text);
}

/* ═══════════════════════════════════════════════════════════ */
/*  RESPONSIVE                                                */
/* ═══════════════════════════════════════════════════════════ */
@media (max-width: 767px) {
  .mobile-nav {
    display: block;
  }

  .category-section {
    padding: 16px;
  }

  .category-section__heading {
    font-size: 20px;
  }
}
</style>
